/**
 * V4.24.46 — Pick Recommended Banks (Web) — mirrors mobile
 * `loan_recommended_banks` screen for Type C Stage 2 (ready_pick_banker).
 *
 * V4.24.81 — Now sources banks from the dedicated
 * `/api/public/v1/loan-cases/:id/recommended-banks` endpoint (which
 * resolves canonical `bank_code`s from bank names via the registry),
 * and submits with the correct `bank_codes[]` payload key.
 *
 * Backend:
 *   GET  /api/public/v1/loan-cases/:id/recommended-banks
 *   POST /api/public/v1/loan-cases/:id/submit-bankers { bank_codes: [...] }
 *
 * Deep-link path: `/loan/:id/pick-bank`.
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Loader2, Landmark, ShieldCheck, ShieldAlert, ShieldX } from 'lucide-react';
import { LoanPageHeader } from './_shared';

const BUCKET_META = {
  suitable:     { label: 'Suitable',     icon: ShieldCheck,  color: 'bg-emerald-50 border-emerald-200', textColor: 'text-emerald-700' },
  caution:      { label: 'Caution',      icon: ShieldAlert,  color: 'bg-amber-50 border-amber-200',     textColor: 'text-amber-700' },
  not_suitable: { label: 'Not Suitable', icon: ShieldX,      color: 'bg-red-50 border-red-200',         textColor: 'text-red-700' },
};

export default function LoanPickBank() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [bankReview, setBankReview] = useState({ suitable: [], caution: [], not_suitable: [] });
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState({}); // bank_key → true
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        // V4.24.81 — Two parallel calls: case detail (for status + gate)
        // and the dedicated recommended-banks endpoint (canonical bank_code).
        const [caseR, banksR] = await Promise.all([
          api.get(`/public/v1/loan-cases/${id}`),
          api.get(`/public/v1/loan-cases/${id}/recommended-banks`),
        ]);
        setCaseData(caseR.data?.data || null);
        const review = banksR.data?.data?.bank_review || {};
        setBankReview({
          suitable: review.suitable || [],
          caution: review.caution || [],
          not_suitable: review.not_suitable || [],
        });
      } catch (e) { toast.error('Failed to load case'); }
      finally { setLoading(false); }
    })();
  }, [id]);

  // V4.24.77 — Type C stage-2 fan-out is ONLY valid after the customer
  // has picked selection_path = C_recommended. If they land here at
  // `ready_pick_banker` with no path (e.g. bookmarked URL or a stale
  // deep-link), bounce them to /score so they can pick Type A/B/C first.
  useEffect(() => {
    if (!caseData) return;
    if (caseData.status === 'ready_pick_banker' && !caseData.selection_path) {
      navigate(`/loan/${id}/score`, { replace: true });
    }
  }, [caseData, id, navigate]);

  // V4.24.81 — When the AICon report only supplies a bank name (no
  // canonical `code`), fall back to the name as the submittable key.
  // Backend `submit-bankers` accepts BOTH codes and names via
  // `_auto_pick_bankers_for_banks`.
  const bankKey = (b) => b.bank_code || b.bank_name;

  const toggle = (key) => setPicked(p => ({ ...p, [key]: !p[key] }));
  const pickedList = Object.entries(picked).filter(([, v]) => v).map(([k]) => k);

  const submit = async () => {
    if (pickedList.length === 0) { toast.error('Pick at least one bank'); return; }
    if (pickedList.length > 3)   { toast.error('Maximum 3 banks'); return; }
    setSubmitting(true);
    try {
      // V4.24.81 — Correct payload key is `bank_codes` (not `banks`).
      // Backend accepts codes or names; both auto-pick a workload-balanced
      // banker per bank.
      const r = await api.post(`/public/v1/loan-cases/${id}/submit-bankers`, { bank_codes: pickedList });
      const ns = r.data?.data?.next_screen;
      toast.success(`Submitted to ${pickedList.length} bank${pickedList.length > 1 ? 's' : ''}. Waiting for offers…`);
      navigate(ns === 'loan_offers' ? '/loan-offers' : `/loan/${id}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      let msg = detail?.message || 'Submit failed';
      if (Array.isArray(detail?.errors)) {
        msg += ': ' + detail.errors.map(x => x.message || `${x.field}`).join('; ');
      }
      if (Array.isArray(detail?.unknown_inputs) && detail.unknown_inputs.length) {
        msg += ` (unknown: ${detail.unknown_inputs.join(', ')})`;
      }
      if (Array.isArray(detail?.missing_bank_codes) && detail.missing_bank_codes.length) {
        msg += ` (no banker registered for: ${detail.missing_bank_codes.join(', ')})`;
      }
      toast.error(msg);
    } finally { setSubmitting(false); }
  };

  if (loading || !caseData) return <div className="p-6 text-gray-500"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…</div>;

  // V4.24.83 — Mobile parity: only show banks whose recommendation
  // status is `suitable`. Caution / not_suitable buckets are dropped
  // (matching mobile `loan_recommended_banks` screen).
  const rb = { suitable: bankReview.suitable || [] };
  const buckets = ['suitable'];
  const isReady = caseData.status === 'ready_pick_banker';

  return (
    <div className="max-w-4xl mx-auto p-3 sm:p-6 space-y-4" data-testid="loan-pick-bank-page">
      <LoanPageHeader caseData={caseData} title="Pick Recommended Banks" />
      <p className="text-sm text-gray-600">
        Choose 1&ndash;3 banks to send your application to from the <b>Suitable</b> list below — these are the banks most
        likely to approve your application based on your AICon assessment.
      </p>

      {!isReady && (
        <Card className="p-4 bg-amber-50 border-amber-200 text-sm text-amber-800" data-testid="pick-bank-locked-banner">
          This case is at status <b>{caseData.loan_status_label || caseData.status}</b>. Bank picking is only available at <b>Ready Pick Bank</b>.
        </Card>
      )}

      {buckets.map((bucket) => {
        const banks = rb[bucket] || [];
        if (banks.length === 0) return null;
        const meta = BUCKET_META[bucket];
        const Icon = meta.icon;
        return (
          <div key={bucket} className="space-y-2" data-testid={`bucket-${bucket}`}>
            <div className={`flex items-center gap-2 ${meta.textColor}`}>
              <Icon className="w-4 h-4" />
              <p className="font-semibold text-sm uppercase tracking-wider">{meta.label} ({banks.length})</p>
            </div>
            <div className="space-y-2">
              {banks.map((b) => {
                const key = bankKey(b);
                return (
                <label
                  key={key}
                  className={`border rounded-lg p-3 flex items-center gap-3 cursor-pointer ${meta.color} hover:bg-opacity-70 ${!isReady ? 'opacity-60 pointer-events-none' : ''}`}
                  data-testid={`bank-${key}`}
                >
                  <Checkbox
                    checked={!!picked[key]}
                    onCheckedChange={() => toggle(key)}
                    disabled={!isReady || (!picked[key] && pickedList.length >= 3)}
                    data-testid={`bank-checkbox-${key}`}
                  />
                  <Landmark className="w-5 h-5 text-gray-500" />
                  <div className="flex-1">
                    <p className="font-semibold">{b.bank_name || b.bank_code}</p>
                    {b.probability != null && <p className="text-xs text-gray-500">Approval probability: {(b.probability * 100).toFixed(0)}%</p>}
                    {b.rate_hint && <p className="text-xs text-gray-500">Estimated rate: {b.rate_hint}</p>}
                    {b.reason && <p className="text-xs text-gray-500 mt-0.5 italic">{b.reason}</p>}
                  </div>
                  <Badge variant="outline" className={meta.textColor}>{meta.label}</Badge>
                </label>
                );
              })}
            </div>
          </div>
        );
      })}

      {Object.keys(rb).every((k) => (rb[k] || []).length === 0) && (
        <Card className="p-6 text-center text-gray-500" data-testid="pick-bank-empty">
          AICon has not returned recommendations yet. Please wait a moment and refresh.
        </Card>
      )}

      {isReady && (
        <div className="sticky bottom-0 bg-white border-t p-3 flex items-center justify-between" data-testid="pick-bank-submit-bar">
          <span className="text-sm text-gray-600">{pickedList.length}/3 selected</span>
          <Button onClick={submit} disabled={submitting || pickedList.length === 0} className="bg-indigo-600 hover:bg-indigo-700" data-testid="pick-bank-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
            Submit to Banks
          </Button>
        </div>
      )}
    </div>
  );
}
